import { useTrackEvent } from '@/mixpanel/useTrackEvent'
import TemplateFromSpaceCreatorModal from '@/modules/templates/TemplateFromSpaceCreator/TemplateFromSpaceCreatorModal'
import { useQueryClient } from '@tanstack/react-query'
import { TSpace } from '@valuecase/common'
import {
  Button2,
  extractTitleFromQuillObject,
  ModalControls,
  Sheet,
  SheetContent,
  useActionPlans,
  useNotifications,
  useOverlay,
  useReadForms,
  useUpdateTaskMutation,
} from '@valuecase/ui-components'
import { SpaceSidebarDataProvider } from '@valuecase/ui-components/src/features/space-sheet/context/SpaceSidebarDataContext'
import SpaceSheetContent from '@valuecase/ui-components/src/features/space-sheet/space-sheet-content'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useMarkSpaceActivitiesAsRead } from '../../hooks/useMarkSpaceActivitiesAsRead'
import { useReadGroupedActivities } from '../../hooks/useReadGroupedActivities'
import { useSpaceComments } from '../../hooks/useSpaceComments'
import { useSpaceWhitelistedUsers } from '../../hooks/useSpaceWhitelistedUsers'
import { useUnreadCommentsCount } from '../../hooks/useUnreadCommentsCount'
import { useUnreadSpaceActivities } from '../../hooks/useUnreadSpaceActivities'
import {
  copySpaceLinkWithToken,
  createGetUrlWithToken,
  openBlock,
  openSpace,
} from '../../utils/spaceUrlUtils'
import { SpaceContextMenu } from '../SpaceContextMenu'
import EditSpaceTeamsDialog from '../spacesList/dialogs/EditSpaceTeamsDialog'
import { SpacesListDto } from '../spacesList/spacesList'
import { useSpaceSheet } from './SpaceSheetContext'
import { useSpaceSidebarView } from '@/hooks/useSpaceSidebarView'
import SpaceDeleteModal from '@/ui-components/SpaceDeleteModal'
import { useDeleteSpace } from '../../hooks/useDeleteSpace'
import { useChangeSpacedArchivedStatus } from '../../hooks/useChangeSpacedArchivedStatus'

export const SpaceSidebar = () => {
  const { trackEvent } = useTrackEvent()
  const { selectedSpace, closeSpaceSheet, selectedSpaceId } = useSpaceSheet()
  const modalControls = useRef<ModalControls | null>(null)
  const overlay = useOverlay()
  const containerRef = useRef<HTMLDivElement>(null)
  const [showDeleteSpaceModal, setShowDeleteSpaceModal] = useState(false)
  const [showWaitingForLoadAnimation, setShowWaitingForLoadAnimation] = useState(false)
  const { deleteSpaceAsync } = useDeleteSpace()
  const { changeSpaceArchivedStatus } = useChangeSpacedArchivedStatus()
  const { error, success } = useNotifications()

  const { settings: viewSettings, updateSettings: updateViewSettings } = useSpaceSidebarView()

  const showCreateTemplateFromSpaceOverlay = useCallback(
    (spaceId: string) => {
      overlay.show(<TemplateFromSpaceCreatorModal controlsRef={modalControls} spaceId={spaceId} />)
    },
    [overlay],
  )
  const [spaceSelectedForTeamEditing, setSpaceSelectedForTeamEditing] = useState<TSpace | null>(
    null,
  )
  const {
    data: actionPlans,
    isLoading: actionPlansLoading,
    error: actionPlansError,
  } = useActionPlans({
    spaceIds: [selectedSpace?.id ?? ''],
    page: 1,
    itemsPerPage: 0,
    skipOwnerFilter: true,
  })

  const {
    data: comments,
    isLoading: commentsLoading,
    error: commentsError,
  } = useSpaceComments(selectedSpace?.rootNodeId ?? '')

  const { data: whitelistedUsers } = useSpaceWhitelistedUsers(selectedSpace?.rootNodeId ?? '', {
    staleTime: 0,
  })

  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useReadGroupedActivities(selectedSpace?.rootNodeId)

  const { data: unreadActivities } = useUnreadSpaceActivities(selectedSpace?.rootNodeId, {
    staleTime: 0,
  })
  const { unreadCount: unreadCommentsCount, markCommentsAsRead } = useUnreadCommentsCount(
    selectedSpace?.rootNodeId,
  )

  const markActivitiesAsRead = useMarkSpaceActivitiesAsRead(selectedSpace?.rootNodeId)

  const handleMarkActivitiesAsRead = useCallback(() => {
    markActivitiesAsRead.mutate()
  }, [markActivitiesAsRead])

  const handleMarkCommentsAsRead = useCallback(async () => {
    if (!selectedSpace?.rootNodeId || !comments) return
    await markCommentsAsRead(comments.map((comment) => comment.id))
  }, [markCommentsAsRead, selectedSpace?.rootNodeId, comments])

  const queryClient = useQueryClient()
  const updateTaskMutation = useUpdateTaskMutation()

  const handleCompleteTask = useCallback(
    async (taskId: string, checked: boolean) => {
      try {
        await updateTaskMutation.mutateAsync({
          id: taskId,
          data: { checked },
        })
        queryClient.invalidateQueries({
          queryKey: ['actionPlans', 1, 0, [selectedSpace?.id ?? ''], undefined, undefined],
        })
        trackEvent({
          event: 'task-completed-space-sheet',
          eventProperties: {
            completed: checked,
          },
        })
        success('Task updated')
      } catch (err) {
        console.error(err)
        error('Error updating task')
      }
    },
    [error, queryClient, selectedSpace?.id, success, trackEvent, updateTaskMutation],
  )

  const {
    data: forms,
    isLoading: formsLoading,
    error: formsError,
  } = useReadForms({
    spaceIds: [selectedSpace?.id ?? ''],
    page: 1,
    itemsPerPage: 100,
    includeDeleted: true,
    includeNotCompleted: true,
    includeSubmissions: true,
    completedAfter: undefined,
    staleTime: 0,
  })

  const handleOpenBlock = useCallback(
    (props: { spaceId: string; blockId?: string; pageId?: string }) => {
      openBlock({
        spaceId: props.spaceId,
        authToken: selectedSpace?.authToken,
        blockId: props.blockId,
        pageId: props.pageId,
      })
    },
    [selectedSpace?.authToken],
  )

  const handleOpenSpace = useCallback(() => {
    if (!selectedSpaceId) return
    openSpace(selectedSpaceId, selectedSpace?.authToken)
  }, [selectedSpaceId, selectedSpace?.authToken])

  const handleCopySpaceLink = useCallback(() => {
    if (!selectedSpaceId) return
    copySpaceLinkWithToken(selectedSpaceId, selectedSpace?.authToken)
    success('Space link copied to clipboard')
  }, [selectedSpaceId, selectedSpace?.authToken, success])

  const handleDeleteSpace = useCallback(async () => {
    if (!selectedSpace) return
    try {
      setShowWaitingForLoadAnimation(true)
      await deleteSpaceAsync(selectedSpace.rootNodeId)
      trackEvent({
        event: 'spaces-delete-confirm',
        eventProperties: {
          indexPageName: 'Delete Space Modal',
          spaceId: selectedSpace.id || 'unknownSpaceId',
        },
      })
      success(`Space '${selectedSpace.companyName}' successfully deleted.`)
      setShowDeleteSpaceModal(false)
      closeSpaceSheet()
    } catch (err) {
      error('There was an error deleting the space. Please try again later.')
    } finally {
      setShowWaitingForLoadAnimation(false)
    }
  }, [selectedSpace, deleteSpaceAsync, trackEvent, success, error, closeSpaceSheet])

  const handleArchiveSpace = useCallback(async () => {
    if (!selectedSpace) return
    try {
      setShowWaitingForLoadAnimation(true)
      await changeSpaceArchivedStatus(selectedSpace.rootNodeId, true)
      success('Space successfully archived')
      setShowDeleteSpaceModal(false)
      closeSpaceSheet()
    } catch (err) {
      error('There was an error archiving the space. Please try again later.')
    } finally {
      setShowWaitingForLoadAnimation(false)
    }
  }, [selectedSpace, changeSpaceArchivedStatus, success, closeSpaceSheet, error])

  const contextMenu = useMemo(
    () => (
      <SpaceContextMenu
        trigger={<Button2 variant='ghost' size='smallIcon' leadingIcon='ellipsis-vertical' />}
        getUrlWithToken={createGetUrlWithToken()}
        space={selectedSpace as TSpace}
        onCopyLinkToSpace={handleCopySpaceLink}
        showCreateTemplateFromSpaceOverlay={showCreateTemplateFromSpaceOverlay}
        setSpaceSelectedForTeamEditing={() => {
          setSpaceSelectedForTeamEditing(selectedSpace)
        }}
        selectSpaceForDeletion={() => selectedSpace}
        setShowDeleteSpaceModal={setShowDeleteSpaceModal}
      />
    ),
    [handleCopySpaceLink, selectedSpace, showCreateTemplateFromSpaceOverlay],
  )

  const getBlockTitle = useCallback((data: any): string | undefined => {
    if (!data?.block?.versions?.[0]?.asset?.data?.title) {
      return 'Block'
    }
    return extractTitleFromQuillObject(data.block.versions[0].asset.data.title) ?? undefined
  }, [])

  return (
    <Sheet open={!!selectedSpaceId} modal={false}>
      <SheetContent
        ref={containerRef}
        className='flex p-0 flex-col min-w-[280px] max-w-[600px] !w-1/3 border-0'
      >
        {spaceSelectedForTeamEditing && (
          <EditSpaceTeamsDialog
            space={spaceSelectedForTeamEditing as unknown as SpacesListDto}
            onOpenChange={() => setSpaceSelectedForTeamEditing(null)}
          />
        )}
        {selectedSpace && (
          <SpaceDeleteModal
            isOpen={showDeleteSpaceModal}
            onOpenChange={setShowDeleteSpaceModal}
            spaceName={selectedSpace.companyName}
            isArchived={selectedSpace.archived}
            isDeleting={showWaitingForLoadAnimation}
            onConfirm={handleDeleteSpace}
            onArchiveClick={handleArchiveSpace}
          />
        )}
        {!selectedSpace ? (
          <div></div>
        ) : (
          <SpaceSidebarDataProvider
            value={{
              getBlockTitle,
              space: selectedSpace,
              actionPlans: {
                data: actionPlans?.items ?? [],
                isLoading: actionPlansLoading,
                error: actionPlansError as unknown as Error | null,
              },
              onCheckTask: handleCompleteTask,
              whitelistedUsers: whitelistedUsers ?? null,
              comments: {
                data: comments ?? [],
                isLoading: commentsLoading,
                error: commentsError as unknown as Error | null,
              },
              activities: {
                data: {
                  activities: activities?.activities ?? [],
                  sessions: activities?.sessions ?? [],
                },
                isLoading: activitiesLoading,
                error: activitiesError as unknown as Error | null,
              },
              onOpenBlock: handleOpenBlock,
              unreadActivitiesCount: unreadActivities?.unreadActivitiesCount ?? 0,
              unreadCommentsCount,
              onMarkActivitiesAsRead: handleMarkActivitiesAsRead,
              onMarkCommentsAsRead: handleMarkCommentsAsRead,
              onOpenSpace: handleOpenSpace,
              onCopySpaceLink: handleCopySpaceLink,
              contextMenu: contextMenu,
              forms: {
                data: forms?.items ?? [],
                isLoading: formsLoading,
                error: formsError as unknown as Error | null,
              },
              viewSettings,
              onUpdateViewSettings: updateViewSettings,
            }}
          >
            <SpaceSheetContent space={selectedSpace} onClose={closeSpaceSheet} />
          </SpaceSidebarDataProvider>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default SpaceSidebar
